import { useState, useEffect } from 'react';
import { clearInterviewPrefetchCache } from '../Pages/Interview/interviewPrefetch';
import { clearHiredProfilesPrefetchCache } from '../Pages/HiredProfiles/hiredProfilesPrefetch';
import { clearOverviewStatsCache } from '../Pages/Overview/overviewStatsCache';
import { startOverviewPrefetch } from '../Pages/Overview/overviewPrefetch';
import { startMyJobsPrefetch } from '../Pages/MyJobs/myJobsPrefetch';
import { clearRecentJobsCacheOnLogout } from './recentJobHook';
import { clearJobsCacheOnLogout } from './jobHook';
import creditServices from '@/Services/creditServices';

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const companyToken = localStorage.getItem('company_token');
      const employerEmail = localStorage.getItem('employer_email');
      const companyAccountId = localStorage.getItem('companyAccountId');

      // Simple check - if we have the required data, consider user authenticated
      if (employerEmail && companyAccountId) {
        setIsAuthenticated(true);
        setUser({
          employer_email: employerEmail,
          companyAccountId: companyAccountId,
          company_name: localStorage.getItem('company_name') || 'Company',
          company_token: companyToken || 'temp-token'
        });
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (userData) => {
    // console.log('Login data received:', userData);
    
    // Map the backend response to what we need
    const companyToken = userData.company_token || userData.token || 'temp-token';
    const companyAccountId = userData.companyAccountId || userData.companyId;
    const employerEmail = userData.employer_email || userData.email;
    const companyName = userData.company_name || 'Company';
    
    // Store in localStorage (same as localhost)
    localStorage.setItem('company_token', companyToken);
    localStorage.setItem('companyAccountId', companyAccountId);
    localStorage.setItem('employer_email', employerEmail);
    localStorage.setItem('company_name', companyName);

    // Store in sessionStorage (same as localhost)
    sessionStorage.setItem('employer_email', employerEmail);
    sessionStorage.setItem('company_token', companyToken);

    // Signed employer token — the unforgeable identity the backend authorizes on
    // (attached to API calls by src/utils/apiAuthInterceptor). Works cross-domain. T0.5-1b.
    if (userData.employer_token) {
      localStorage.setItem('employer_token', userData.employer_token);
      sessionStorage.setItem('employer_token', userData.employer_token);
    }

    // Set for a team login only. Removed for an owner, so a person signing in after
    // a colleague on the same browser never inherits their limits.
    if (userData.company_user) {
      localStorage.setItem('company_user', JSON.stringify(userData.company_user));
    } else {
      localStorage.removeItem('company_user');
    }
    
    // Set authentication state immediately (same as localhost)
    setIsAuthenticated(true);
    setUser({
      company_token: companyToken,
      companyAccountId: companyAccountId,
      employer_email: employerEmail,
      company_name: companyName
    });

    // Prefetch Overview and My Jobs so dashboard and job list load fast when user lands there
    // startOverviewPrefetch();
    // startMyJobsPrefetch();

    setIsLoading(false);

    creditServices.preloadCosts().catch((error) => {
      console.warn("[useAuth] Failed to preload credit costs:", error);
    });
  };

  const logout = async () => {
    // Enterprise-grade logout: nuke server-side session FIRST, then client
    // state. Without the server call, the Flask session cookie stays valid
    // and any subsequent request re-uses the old identity — even if the
    // frontend thinks it's logged out. Best-effort — never blocks the UI
    // logout even if the network call fails.
    try {
      const { getApiUrl } = await import('../../config/api');
      await fetch(getApiUrl('/api/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (_) {
      // Network failure shouldn't prevent client-side logout.
    }

    // ALL identity keys must be cleared on logout. Historically only
    // employer_* keys were cleared, so a prior admin login could leave
    // `admin_email` sitting in localStorage. The savedInterviewersApi and
    // similar helpers then picked it up (their ACTOR_KEYS prioritise
    // admin_email FIRST) and sent it as the scope in subsequent requests,
    // scoping the new user's data under the previous admin's email.
    // Clearing everything here prevents cross-session contamination.
    const IDENTITY_STORAGE_KEYS = [
      // Employer / company owner
      'employer_email',
      'employer_password',
      'employer_token',
      'company_email',
      'company_token',
      'company_name',
      'companyAccountId',
      'company_user',
      // Super admin / platform admin
      'admin_email',
      'admin_token',
      'admin_id',
      'admin_role',
      'is_super_admin',
      // Third-party vendor recruiter
      'recruiter_email',
      'recruiter_token',
      // Interview organizer
      'organizer_email',
      'organizer_token',
      // Any other identity-shaped keys that may have accumulated
      'actor_email',
      'user_email',
      'user_token',
    ];
    for (const key of IDENTITY_STORAGE_KEYS) {
      try { localStorage.removeItem(key); } catch (_) {}
      try { sessionStorage.removeItem(key); } catch (_) {}
    }
    clearInterviewPrefetchCache();
    clearHiredProfilesPrefetchCache();
    clearOverviewStatsCache();
    clearRecentJobsCacheOnLogout();
    clearJobsCacheOnLogout();
    setIsAuthenticated(false);
    setUser(null);
  };

  return {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    checkAuthStatus
  };
};
